import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Table, Button, Card, Space, Typography, Tag, message } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const AdminPayments = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const API_BASE = import.meta.env.VITE_API_BASE || "http://localhost:4242";
    const ADMIN_API_KEY = import.meta.env.VITE_ADMIN_API_KEY || "";

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE}/api/admin/payments-report`, {
                headers: ADMIN_API_KEY ? { "x-api-key": ADMIN_API_KEY } : {},
            });
            setData(response.data.data);
        } catch (err) {
            console.error("Error fetching report:", err);
            message.error("Failed to fetch payment records");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const downloadExcel = () => {
        if (data.length === 0) {
            message.warning("No data to download");
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Payments");

        // Generate buffer
        XLSX.writeFile(workbook, `Stripe_Payments_30Days_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const downloadCSV = () => {
        if (data.length === 0) {
            message.warning("No data to download");
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(data);
        const csv = XLSX.utils.sheet_to_csv(worksheet);

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `Stripe_Payments_30Days_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => new Date(text).toLocaleString(),
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount, record) => `$${(amount / 100).toFixed(2)} ${record.currency?.toUpperCase() || 'USD'}`,
        },
        {
            title: 'Order #',
            dataIndex: 'orderno',
            key: 'orderno',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => (
                <Tag color={type === 'Commercial Cleaning' ? 'blue' : 'green'}>
                    {type}
                </Tag>
            ),
            filters: [
                { text: 'Commercial Cleaning', value: 'Commercial Cleaning' },
                { text: 'New Business', value: 'New Business' },
            ],
            onFilter: (value, record) => record.type === value,
        },
        {
            title: 'Stripe Status',
            dataIndex: 'stripe_status',
            key: 'stripe_status',
            render: (status) => (
                <Tag color={status === 'succeeded' ? 'success' : 'default'}>
                    {status || 'N/A'}
                </Tag>
            ),
        },
        {
            title: 'Local Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'succeeded' || status === 'created' ? 'success' : 'processing'}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Download',
            dataIndex: 'download_status',
            key: 'download_status',
            render: (status) => (
                <Tag color={status === 'Yes' ? 'green' : 'orange'}>
                    {status === 'Yes' ? 'Downloaded' : 'Pending'}
                </Tag>
            ),
        },
        {
            title: 'Receipt',
            dataIndex: 'receipt_url',
            key: 'receipt_url',
            render: (url) => url ? (
                <Button type="link" href={url} target="_blank" size="small">
                    View
                </Button>
            ) : 'N/A',
        },
        {
            title: 'Asked ZIP',
            dataIndex: 'gls_zipcode',
            key: 'gls_zipcode',
        },
        {
            title: 'Leads',
            dataIndex: 'leads_count',
            key: 'leads_count',
        },
        {
            title: 'County',
            dataIndex: 'leads_county',
            key: 'leads_county',
        },
        {
            title: 'Lead State',
            dataIndex: 'leads_state',
            key: 'leads_state',
        },
        {
            title: 'ZIPs Used',
            dataIndex: 'zips_used',
            key: 'zips_used',
            ellipsis: true,
        },
        {
            title: 'Card',
            key: 'card',
            render: (_, r) => {
                const brand = r.card_brand || '';
                const last4 = r.card_last4 || '';
                const funding = r.card_funding || '';
                if (!brand && !last4) return r.payment_method_type || 'N/A';
                return `${brand}${funding ? ` (${funding})` : ''}${last4 ? ` •••• ${last4}` : ''}`.trim();
            },
        },
        {
            title: 'Failure Reason',
            dataIndex: 'failure_reason',
            key: 'failure_reason',
            ellipsis: true,
            render: (text, r) => {
                if (!text) return r.stripe_status === 'succeeded' ? '—' : '';
                return <Text type="danger">{text}</Text>;
            },
        },
        {
            title: 'Billing ZIP',
            dataIndex: 'zipcode',
            key: 'zipcode',
        }
    ];

    return (
        <div className="p-8 bg-gray-50 min-h-screen">
            <Card className="shadow-md">
                <div className="flex flex-col md:flex-row justify-between items-center mb-6">
                    <Title level={2}>Payment Records (Last 30 Days)</Title>
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchData}
                            loading={loading}
                        >
                            Refresh
                        </Button>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={downloadExcel}
                            className="bg-green-600 hover:bg-green-700"
                        >
                            Export Excel
                        </Button>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={downloadCSV}
                        >
                            Export CSV
                        </Button>
                    </Space>
                </div>

                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey={(record) => record.id || record.payment_intent_id || Math.random()}
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        </div>
    );
};

export default AdminPayments;
